/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { AiringOffer, ProgramType } from 'client-code/airing';
import { Locale } from '../../../../core/locale/Locale';
import { LocaleKeys } from '../../../../core/locale/LocaleKeys';
import { AppUtils } from '../../../../core/AppUtils';
import { LEFT_QUOTE, RIGHT_QUOTE } from '../../../../config';
import { TOfferPreviewCustomData } from '../../../../core/types/TOfferPreviewCustomData';
import { ServicesClientImpl } from '../../../../core/impls/ServicesClientImpl';

export class InfoBannerUtils {
    private airingData: AiringOffer[] | undefined;

    private previewData: TOfferPreviewCustomData | undefined;

    private checkAiringDataLength: number = 0;

    channelTitleText: string = '';

    channelTitleTextNext: string = '';

    private timeParentalControlsRatingSeperator: boolean = false;

    private parentalControlsRatingSeasonEpisodeSeperator: boolean = false;

    private parentalControlsRatingStarRatingSeperator: boolean = false;

    constructor(airingOffer?: AiringOffer[], offerPreview?: TOfferPreviewCustomData) {
        this.airingData = airingOffer;
        this.previewData = offerPreview;
        this.checkAiringDataLength = this.airingData?.length || 0;
        this._formatChannelTitle();
        this._formatChannelTitleNext();
        this._evaluateMetadataSeperators();
    }

    getEpisodeTitle = () => {
        const subtitle = this.airingData && this.airingData[0]?.banner?.subtitle;
        const episodeTitle = this.previewData?.contentPreview?.episodeTitle;
        if (this.hasAdult()) return Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN);
        if (subtitle) return subtitle;
        if (episodeTitle) return episodeTitle;
        return '';
    };

    hasGenre = () => {
        if (!this.airingData) {
            return false;
        }
        return !!(
            this.checkAiringDataLength &&
            this.airingData[0].logging.genre &&
            this.airingData[0].logging.genre !== '' &&
            this.airingData[0].logging.genre !== undefined
        );
    };

    generateGenre() {
        if (!this.airingData || this.checkAiringDataLength === 0) {
            return;
        }
        const COMMA = ',';
        const genreText = this.airingData[0].logging.genre;
        let genreLength = 23;
        if (this.getProgramType() === ProgramType.Movie || this.getFirstAireddate() === '')
            genreLength = 27;

        if (genreText && genreText.length > genreLength) {
            const genreString = genreText.slice(0, genreLength);

            let genreList =
                genreString[genreLength - 1] === COMMA
                    ? genreString.slice(0, genreLength - 1)
                    : genreString.split(COMMA);

            if (Array.isArray(genreList)) {
                genreList.pop();
                genreList = genreList.join(COMMA);
            }
            return genreList;
        }
        return genreText;
    }

    getGenre = () => {
        if (!this.airingData || this.checkAiringDataLength === 0) {
            return false;
        }
        if (this.hasGenre() && this.airingData[0].logging.genre !== undefined) {
            return this.generateGenre();
        }
        return false;
    };

    getProgramType = (): string => {
        return (this.airingData && this.airingData[0]?.banner?.programType) || '';
    };

    getProgramTypeNext = (): string => {
        return (this.airingData && this.airingData[1]?.banner?.programType) || '';
    };

    getFirstAireddate = (): string | null => {
        if (this.getProgramType() === ProgramType.Movie) return null;
        const originalAirDate = this.airingData && this.airingData[0]?.logging?.originalAirDate;
        const firstAiredDate = this.previewData?.contentPreview?.firstAiredDate;
        const sAiringDate = originalAirDate || firstAiredDate;
        if (!sAiringDate) return null;
        const airingEpoch = new Date(sAiringDate).getTime();
        const nowEpoch = new Date().getTime();
        const prefixText =
            airingEpoch >= nowEpoch
                ? Locale.getString(LocaleKeys.FIRST_AIRS)
                : Locale.getString(LocaleKeys.FIRST_AIRED);
        return AppUtils.strftime(sAiringDate, `${prefixText} %M/%d/%y`);
    };

    getVideoResolution = () => {
        let videoResolution = '';
        if (this.airingData && this.airingData[0]?.logging?.videoResolution) {
            videoResolution = this.airingData[0]?.logging?.videoResolution;
        } else if (this.previewData?.videoResolution) {
            videoResolution = this.previewData.videoResolution;
        } else return '';
        return videoResolution;
    };

    hasAdult = (): boolean => {
        if (!this.airingData) {
            return false;
        }
        if (this.checkAiringDataLength && this.airingData[0].parentalControls.isAdult)
            return this.airingData[0].parentalControls.isAdult;

        return !!this.previewData?.contentPreview?.isAdult;
    };

    hasAdultNext = (): boolean => {
        if (!this.airingData) {
            return false;
        }
        if (this.checkAiringDataLength && this.airingData[1].parentalControls.isAdult) return true;
        return false;
    };

    getDescription = () => {
        if (this.previewData?.contentPreview?.description && !this.hasAdult())
            return this.previewData.contentPreview?.description;

        return Locale.getString(LocaleKeys.LIVE_TV_FULL_INFO_BANNER_DESCRIPTION_NOT_AVAILABLE);
    };

    hasCC = (): boolean => {
        return !!(this.previewData && this.previewData.hasCc);
    };

    generateCast = () => {
        const COMMA = ',';
        const castText = this.previewData?.contentPreview?.creditString;
        const castLength = 125;
        if (castText && castText.length > castLength) {
            const castString = castText.slice(0, castLength);

            let castList =
                castString[castLength - 1] === COMMA
                    ? castString.slice(0, castLength - 1)
                    : castString.split(COMMA);

            if (Array.isArray(castList)) {
                castList.pop();
                castList = castList.join(COMMA);
            }
            return castList;
        }
        return castText;
    };

    getCast = () => {
        return this.generateCast();
    };

    hasSAP = (): boolean => {
        return !!(this.previewData && this.previewData.hasSap);
    };

    getMovieYear = (): number => {
        if (this.airingData && this.airingData[0]?.banner?.movieYear)
            return this.airingData[0]?.banner?.movieYear;

        return 0;
    };

    getMovieYearNext = (): number => {
        if (this.airingData && this.airingData[1]?.banner?.movieYear)
            return this.airingData[1]?.banner?.movieYear;
        return 0;
    };

    getChannelTitle = (): string => {
        if (this.hasAdult()) return Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN);
        if (this.airingData && this.airingData[0]?.banner?.title)
            return this.airingData[0]?.banner?.title;
        if (this.previewData?.contentPreview?.title) return this.previewData?.contentPreview?.title;
        return Locale.getString(LocaleKeys.LIVE_TV_FULL_INFO_BANNER_TITLE_NOT_AVAILABLE);
    };

    getChannelTitleNext = (): string => {
        if (this.hasAdult()) return Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN);
        if (this.airingData && this.airingData[1]?.banner?.title)
            return this.airingData[1]?.banner?.title;
        if (this.previewData?.contentPreview?.title) return this.previewData?.contentPreview?.title;
        return Locale.getString(LocaleKeys.LIVE_TV_FULL_INFO_BANNER_TITLE_NOT_AVAILABLE);
    };

    private _formatChannelTitle = () => {
        if (
            this.getProgramType() === ProgramType.Movie &&
            this.getMovieYear() &&
            !this.hasAdult()
        ) {
            this.channelTitleText = `${this.getChannelTitle()} (${this.getMovieYear()})`;
        } else {
            this.channelTitleText = this.getChannelTitle();
        }
    };

    private _formatChannelTitleNext = () => {
        if (
            this.getProgramTypeNext() === ProgramType.Movie &&
            !this.hasAdultNext() &&
            this.getMovieYearNext()
        ) {
            this.channelTitleTextNext = `${this.getChannelTitleNext()} (${this.getMovieYearNext()})`;
        } else {
            this.channelTitleTextNext = this.getChannelTitleNext();
        }
    };

    getChannelTitleText = (): string => {
        return (
            this.channelTitleText ||
            Locale.getString(LocaleKeys.LIVE_TV_FULL_INFO_BANNER_TITLE_NOT_AVAILABLE)
        );
    };

    getChannelTitleTextNext = (): string => {
        return (
            this.channelTitleTextNext ||
            Locale.getString(LocaleKeys.LIVE_TV_FULL_INFO_BANNER_TITLE_NOT_AVAILABLE)
        );
    };

    hasNew = (): boolean => {
        const isNewFromAiring = this.airingData && this.airingData[0]?.banner?.isNew;
        const isNewFromPreview = this.previewData?.contentPreview?.isNew;
        return (!!isNewFromAiring || !!isNewFromPreview);
    };

    hasNewNext = (): boolean => {
        if (!this.airingData || this.checkAiringDataLength === 0) {
            return false;
        }
        return !!(this.airingData && this.airingData[1]?.banner?.isNew);
    };

    getSeasonEpisode = (): any => {
        if (
            this.airingData &&
            this.airingData[0]?.banner?.seasonNumber &&
            this.airingData[0]?.banner?.episodeNumber
        ) {
            return AppUtils.generateSeasonEpisodeText(
                this.airingData[0].banner.seasonNumber,
                this.airingData[0].banner.episodeNumber,
            );
        }

        const contentPreview = this.previewData?.contentPreview;
        if (contentPreview?.seasonNumber && contentPreview?.episodeNumber) {
            return AppUtils.generateSeasonEpisodeText(
                contentPreview.seasonNumber,
                contentPreview.episodeNumber,
            );
        }
        return null;
    };

    getParentalControlsRating = (): any => {
        if (this.airingData && this.airingData[0]?.parentalControls?.rating)
            return this.airingData[0]?.parentalControls?.rating;
        if (this.previewData?.contentPreview?.internalRatingList)
            return this.previewData?.contentPreview?.internalRatingList;
        return null;
    };

    getStartTime = (): string => {
        if (this.airingData && this.airingData[0]?.startTime) return this.airingData[0].startTime;
        if (this.previewData?.contentPreview?.startTime)
            return this.previewData?.contentPreview?.startTime;
        return '';
    };

    getDuration = (): number => {
        if (this.airingData && this.airingData[0]?.duration) return this.airingData[0].duration;
        if (this.previewData?.contentPreview?.duration)
            return this.previewData?.contentPreview?.duration;
        return 0;
    };

    getEndTime = (): Date => {
        if (this.getDuration() !== 0 && this.getStartTime() !== '')
            return AppUtils.addSecondsToDateTime(new Date(this.getStartTime()), this.getDuration());
        if (this.previewData?.contentPreview?.endTime)
            return new Date(this.previewData?.contentPreview?.endTime);
        return new Date('');
    };

    getTime(): any {
        if (this.getStartTime() !== '' && this.getEndTime()) {
            return AppUtils.generateTimeText(new Date(this.getStartTime()), this.getEndTime());
        }
        return null;
    }

    getStarRating = (): number => {
        return (
            this.airingData?.[0]?.banner?.starRating ||
            this.previewData?.contentPreview?.starRating ||
            0
        );
    };

    isInternalRatingLoaded = (): boolean => {
        const services = new ServicesClientImpl();
        let flag = false;
        const parentalControlsRating = this.getParentalControlsRating();
        if (parentalControlsRating) {
            parentalControlsRating.forEach(({ typeId, valueId }: any) => {
                const servieCall = services.getInternalRatingName(typeId + valueId);
                if (servieCall) {
                    flag = true;
                }
            });
        }
        return flag;
    };

    private _evaluateMetadataSeperators = () => {
        const isInternalRatingLoaded = this.isInternalRatingLoaded();
        const getTime = this.getTime();

        this.timeParentalControlsRatingSeperator = !!(getTime && isInternalRatingLoaded);
        this.parentalControlsRatingSeasonEpisodeSeperator = !!(
            (getTime || isInternalRatingLoaded) &&
            this.getSeasonEpisode() !== null
        );
        this.parentalControlsRatingStarRatingSeperator = !!(
            (getTime || isInternalRatingLoaded) &&
            this.getStarRating() !== 0
        );
    };

    getTimeParentalControlsRatingSeperator() {
        return this.timeParentalControlsRatingSeperator;
    }

    getParentalControlsRatingSeasonEpisodeSeperator() {
        return this.parentalControlsRatingSeasonEpisodeSeperator;
    }

    getParentalControlsRatingStarRatingSeperator() {
        return this.parentalControlsRatingStarRatingSeperator;
    }

    getAudioSubtitlePopupTitle = () => {
        let title = '';
        const seasonEpisode = this.getSeasonEpisode();
        const episodeTitle = this.getEpisodeTitle();
        title += `${this.getChannelTitleText()}`;
        if (seasonEpisode) {
            title += ` - ${seasonEpisode}`;
        }
        if (!seasonEpisode && episodeTitle) {
            title += ` - ${LEFT_QUOTE}${episodeTitle}${RIGHT_QUOTE}`;
        } else if (episodeTitle) {
            title += ` ${LEFT_QUOTE}${episodeTitle}${RIGHT_QUOTE}`;
        }
        return title;
    };

    getShowAttributes = (currentPlaybackResolution?: number) => {
        const showAttributes: string[] = [];
        if (this.getVideoResolution().length) {
            showAttributes.push(this.getVideoResolution().toLocaleUpperCase());
        }

        if (this.hasCC()) {
            showAttributes.push('CC');
        }

        if (this.hasSAP()) {
            showAttributes.push('SAP');
        }

        if (currentPlaybackResolution) {
            showAttributes.push(`${currentPlaybackResolution}p`);
        }

        if (showAttributes.length === 0) {
            return false;
        }

        const showAttributesString = showAttributes.join(', ');

        return showAttributesString;
    };

    getIsAvailableInSocu = (): boolean => {
        if (!this.previewData) {
            return false;
        }
        return !!this.previewData?.isAvailableInSocu;
    };
}
